// Die Kette einer Anlage (E3c, S2 – PLAN.md, „Die Kette einer Anlage“;
// Prüfung des Plans, reviews/2026-11-02-plan-pruefung-e3c.md, Befunde 6, 7,
// 9, 14): je Anlage acht Schritte und ihr Stand – EINE Rechnung für jede
// Seite. „Zu tun“, die Leiste in der Akte, die Übersicht und die Kiste der
// Anlage (`anlage.kiste`, gesetzt nur vom Server) rechnen alle hiermit.
// Reine Logik: Was an der Anlage steht, kommt als Fakten herein
// (`KettenFakten`, gelesen in server/src/crm/kette.ts), heraus kommt der
// Stand jedes Schritts mit einem kurzen Grund in Worten.
//
// Die acht Schritte (für jede Firma gleich – H23):
//   1 Besichtigung   draußen; erledigt, sobald sie gesendet ist
//   2 Termin         der Montagetermin – ein Tag (ein Text allein reicht nicht:
//                    die Lieferung rechnet vom Tag, O100)
//   3 Dachplanung    der K2-Plan (PDF) liegt bei der Anlage – das Excel von K2
//                    kommt mit S5 dazu und hält den Schritt bis dahin nicht auf
//   4 Freigeben      wie bisher ein Knopf; geht, sobald die Besichtigung gesendet ist
//   5 Bestellt       je Teil beim Lieferanten (unten, „Was bestellt sein muss“)
//   6 Dachpartie beauftragt
//   7 Bodenpartie eingeteilt
//   8 Montiert       von Hand in der Akte
//
// Die Regeln:
//  - 5 bis 7 gehen erst nach dem Freigeben und wenn der Termin steht – dann
//    in beliebiger Reihenfolge (Gernot, 2.11.: Freigeben bleibt EIN Knopf, die
//    drei Schritte danach sind nur getrennt, weil jeder an jemand anderen geht).
//  - Erledigt ist erledigt: Eine erledigte Bestellung bleibt erledigt, auch
//    wenn der Termin später fehlt. Soll ein Schritt wieder offen sein, legt
//    der Server eine NEUE Zeile an, die die erledigte ersetzt (gilt seit /
//    ersetzt, Befund 7) – mit dem Anlass als Grund (ablauf-vertrag.ts,
//    ZEILEN_ANLAESSE). Eine geltende, nicht erledigte Zeile, deren Vorgängerin
//    erledigt war: „wieder offen“.
//  - „Schon erledigt“ (ohne System, mit Person) zählt als erledigt; was das
//    Update selbst übernommen hat (Anlass „vor-dem-update“, ohne Person),
//    heißt „vor dem Update erledigt“ – sichtbar, nicht still (Befund 14).
//  - Kam nach dem Beauftragen der Dachpartie ein Nachtrag oder eine neue
//    Dachplanung, bleibt der Schritt erledigt, trägt aber den Hinweis
//    „Unterlage erneut senden?“ (Befund 9); ebenso die Bodenpartie nach einem
//    Nachtrag („Unterlage erneut drucken?“).
//  - Montiert: alles erledigt – was nie abgehakt wurde, mit dem Grund „die
//    Anlage ist montiert“.
//
// Was bestellt sein muss (Schritt 5): Unterkonstruktion und Module immer
// (H27); Wechselrichter und Speicher nur, wenn eine Bestellung dafür angelegt
// ist – Manuel verhandelt noch, ob Gautzsch sie einlagert und mitliefert
// (H30). Erledigt ist der Schritt, wenn jedes dieser Teile eine geltende
// Bestellung hat und jede geltende Bestellung erledigt ist (per Mail oder
// „schon erledigt“). So braucht auch eine Nachbestellung desselben Teils ihr
// eigenes Häkchen.
//
// Die Kiste der Anlage (`kisteAusKette`): die Kiste des ersten Schritts, der
// noch nicht erledigt ist – gezählt werden nur die Schritte, durch die eine
// Anlage „wandert“ (Besichtigung und Freigeben → Sales/Doku, Bestellt →
// Bestellung, Dachpartie, Bodenpartie und Montiert → Montage); montiert →
// Inbetriebnahme, die nächste Station. Termin und Dachplanung sind seit E3b
// Aufgaben-Spalten, durch die keine Anlage wandert (Gernot, 22.10.2026; die
// Übersicht zeigt dort „fehlt bei …“) – eine Anlage ohne Termin bleibt
// sichtbar, wo sie steht.

import { ANLASS_WORTE, BESTELL_TEIL_NAMEN, BESTELL_TEILE, type BestellTeil, type EinsatzArt, type ZeilenAnlass } from "./ablauf-vertrag.ts";
import type { AufnahmeStandAmServer } from "./aufnahme-vertrag.ts";

// --- Die Schritte -------------------------------------------------------------------------

export const KETTEN_SCHRITTE = ["besichtigung", "termin", "dachplanung", "freigeben", "bestellt", "dachpartie", "bodenpartie", "montiert"] as const;
export type KettenSchritt = (typeof KETTEN_SCHRITTE)[number];

/** Der Name je Schritt – so steht er in der Leiste und auf den Karten. */
export const KETTEN_SCHRITT_NAMEN: Record<KettenSchritt, string> = {
  besichtigung: "Besichtigung",
  termin: "Termin",
  dachplanung: "Dachplanung",
  freigeben: "Freigeben",
  bestellt: "Bestellt",
  dachpartie: "Dachpartie beauftragt",
  bodenpartie: "Bodenpartie eingeteilt",
  montiert: "Montiert",
};

/**
 * Der Stand eines Schritts:
 *  - erledigt;
 *  - vor-dem-update: erledigt, vom Update übernommen (ohne Person);
 *  - offen: geht jetzt;
 *  - wieder-offen: war erledigt, dann hat sich etwas geändert – geht jetzt, mit Grund;
 *  - geht-noch-nicht: wartet auf einen Schritt davor (der Grund sagt, auf welchen).
 */
export const SCHRITT_STAENDE = ["erledigt", "vor-dem-update", "offen", "wieder-offen", "geht-noch-nicht"] as const;
export type SchrittStand = (typeof SCHRITT_STAENDE)[number];

/** Der Stand in Worten – kurz, für die Leiste. */
export const SCHRITT_STAND_WORTE: Record<SchrittStand, string> = {
  erledigt: "erledigt",
  "vor-dem-update": "vor dem Update erledigt",
  offen: "offen",
  "wieder-offen": "wieder offen",
  "geht-noch-nicht": "geht noch nicht",
};

/** Zählt als erledigt – auch „vor dem Update“. */
export function istErledigt(stand: SchrittStand): boolean {
  return stand === "erledigt" || stand === "vor-dem-update";
}

/** Was immer bestellt sein muss; Wechselrichter und Speicher nur, wenn eine Bestellung dafür da ist (H30). */
export const PFLICHT_TEILE: readonly BestellTeil[] = ["unterkonstruktion", "module"];

// --- Die Fakten --------------------------------------------------------------------------

/** Eine geltende Bestellung oder ein geltender Einsatz (`ersetzt_am` leer) – nur, was die Kette braucht. */
export interface KettenZeile {
  /** Erledigt (bestellt, beauftragt, eingeteilt oder „schon erledigt“) – wann, ISO; null: noch offen. */
  erledigtAm: string | null;
  /** Warum es die Zeile gibt (ZEILEN_ANLAESSE) – null: der normale Weg. */
  anlass: ZeilenAnlass | null;
  /** „Schon erledigt“ ohne System (mit Person oder vom Update). */
  schonErledigt: boolean;
  /** War die Zeile, die diese ersetzt, erledigt? Dann ist sie offen „wieder offen“. */
  vorgaengerErledigt: boolean;
}

export interface KettenBestellung extends KettenZeile {
  teil: BestellTeil;
}

export interface KettenEinsatz extends KettenZeile {
  art: EinsatzArt;
}

/** Was an einer Anlage steht – alles, woraus die Kette rechnet. Zeitpunkte als ISO-Text, Tage als „JJJJ-MM-TT“. */
export interface KettenFakten {
  /**
   * Die maßgebliche Besichtigung: die jüngste gesendete – gibt es keine, die
   * jüngste überhaupt (draußen in Aufnahme); null: noch keine begonnen.
   */
  besichtigung: { stand: AufnahmeStandAmServer } | null;
  montagetermin: { am: string | null; text: string | null };
  k2: { pdf: boolean; excel: boolean; uebersprungen: boolean };
  /** Die geltenden Bestellungen und Einsätze. */
  bestellungen: readonly KettenBestellung[];
  einsaetze: readonly KettenEinsatz[];
  /** Der jüngste gesendete Nachtrag an einer Besichtigung der Anlage – null: keiner. */
  letzterNachtragAm: string | null;
  /** Wann die Dachplanung zuletzt von draußen geändert wurde (anlage.k2_geaendert_am). */
  k2GeaendertAm: string | null;
  /** Seit S7: wann der Excel-Report von K2 zuletzt ein anderer wurde – vom Gerät oder aus dem Büro (anlage.k2_excel_geaendert_am). */
  k2ExcelGeaendertAm: string | null;
  montiertAm: string | null;
}

// --- Das Ergebnis ------------------------------------------------------------------------------

export interface SchrittInDerKette {
  schritt: KettenSchritt;
  name: string;
  stand: SchrittStand;
  /** Ein kurzer Satz, warum – „geht, sobald der Termin steht“; null: nichts zu sagen. */
  grund: string | null;
  /** Ein Hinweis, der den Stand nicht ändert – „Unterlage erneut senden?“; null: keiner. */
  hinweis: string | null;
}

export interface Kette {
  /** Alle acht, in ihrer Reihenfolge. */
  schritte: SchrittInDerKette[];
  /** Was jetzt geht – offen oder wieder offen –, in der Reihenfolge der Kette. */
  jetzt: KettenSchritt[];
  /** Montiert: alles erledigt. */
  fertig: boolean;
}

// --- Kleine Regeln, die auch andere brauchen ---------------------------------------------------

/** Steht der Termin? Ein Tag muss es sein – ein Text allein („Partie 2, 3 Tage“) reicht nicht. */
export function terminSteht(termin: { am: string | null }): boolean {
  return termin.am !== null;
}

/** Liegt die Dachplanung bei der Anlage? Der K2-Plan (PDF) – das Excel kommt mit S5 dazu. */
export function dachplanungDa(k2: { pdf: boolean }): boolean {
  return k2.pdf;
}

const spaeter = (a: string | null, b: string | null): boolean => a !== null && (b === null || Date.parse(a) > Date.parse(b));

// --- Die Rechnung ---------------------------------------------------------------------------------

type Teilstand = Pick<SchrittInDerKette, "stand" | "grund" | "hinweis">;

const erledigt = (grund: string | null, hinweis: string | null = null): Teilstand => ({ stand: "erledigt", grund, hinweis });
const offen = (grund: string | null): Teilstand => ({ stand: "offen", grund, hinweis: null });
const gehtNochNicht = (grund: string): Teilstand => ({ stand: "geht-noch-nicht", grund, hinweis: null });

/** Was nach „Termin geändert“ zu tun ist – je Schritt. */
const NOCHMAL: Record<"bestellt" | "dachpartie" | "bodenpartie", string> = {
  bestellt: "Mail erneut öffnen",
  dachpartie: "Mail erneut öffnen",
  bodenpartie: "Unterlage erneut drucken",
};

/** Der Grund für „wieder offen“ aus dem Anlass der Zeile. */
function wiederOffenGrund(anlass: ZeilenAnlass | null, schritt: keyof typeof NOCHMAL): string {
  const was = anlass === null || anlass === "vor-dem-update" ? "Geändert" : ANLASS_WORTE[anlass];
  return `${was} – ${NOCHMAL[schritt]}`;
}

/**
 * Der Stand eines Schritts aus seinen geltenden Zeilen – für Bestellt (alle
 * Teile zusammen), Dachpartie und Bodenpartie gleich. `fehlt`: was noch
 * keine Zeile hat, aber eine braucht (bei der Bestellung die Pflicht-Teile).
 */
function ausZeilen(
  zeilen: readonly KettenZeile[],
  fehlt: boolean,
  schritt: keyof typeof NOCHMAL,
  worte: { erledigt: string; offen: string },
  vorbedingung: string | null,
): Teilstand {
  const alleErledigt = !fehlt && zeilen.length > 0 && zeilen.every((z) => z.erledigtAm !== null);
  if (alleErledigt) {
    if (zeilen.every((z) => z.anlass === "vor-dem-update")) return { stand: "vor-dem-update", grund: "vor dem Update ohne System erledigt", hinweis: null };
    if (zeilen.every((z) => z.schonErledigt)) return erledigt("als schon erledigt vermerkt");
    return erledigt(worte.erledigt);
  }
  if (vorbedingung !== null) return gehtNochNicht(vorbedingung);
  const wieder = zeilen.find((z) => z.erledigtAm === null && z.vorgaengerErledigt);
  if (wieder) return { stand: "wieder-offen", grund: wiederOffenGrund(wieder.anlass, schritt), hinweis: null };
  return offen(worte.offen);
}

/** Wann der Schritt zuletzt erledigt wurde – die jüngste erledigte geltende Zeile. */
function zuletztErledigt(zeilen: readonly KettenZeile[]): string | null {
  let juengste: string | null = null;
  for (const z of zeilen) if (spaeter(z.erledigtAm, juengste)) juengste = z.erledigtAm;
  return juengste;
}

/** Die Kette einer Anlage – alle acht Schritte mit Stand, Grund und Hinweis. */
export function ketteRechnen(f: KettenFakten): Kette {
  const stand = f.besichtigung?.stand ?? null;
  const gesendet = stand === "zu_pruefen" || stand === "freigegeben";
  const freigegeben = stand === "freigegeben";
  const termin = terminSteht(f.montagetermin);

  const besichtigung: Teilstand = gesendet
    ? erledigt("an das Büro gesendet")
    : offen(stand === "in_aufnahme" ? "draußen in Aufnahme – noch nicht gesendet" : "noch keine Besichtigung");

  const terminSchritt: Teilstand = termin
    ? erledigt("Montagetermin steht")
    : offen(f.montagetermin.text?.trim() ? "nur ein Text eingetragen – noch kein Tag" : "noch kein Montagetermin");

  const dachplanung: Teilstand = dachplanungDa(f.k2)
    ? erledigt(f.k2.excel ? "K2-Plan und Excel-Report liegen bei der Anlage" : "K2-Plan liegt bei der Anlage")
    : offen(
        f.k2.excel
          ? "der Excel-Report ist da, der K2-Plan (PDF) fehlt noch"
          : f.k2.uebersprungen
            ? "übersprungen – der K2-Plan kann jederzeit nachkommen"
            : "noch kein K2-Plan",
      );

  const freigeben: Teilstand = freigegeben
    ? erledigt("freigegeben")
    : gesendet
      ? offen("Material ansehen und freigeben")
      : gehtNochNicht("geht, sobald die Besichtigung gesendet ist");

  // 5 bis 7: nach dem Freigeben, sobald der Termin steht.
  const vorbedingung =
    !freigegeben && !termin ? "geht nach dem Freigeben, sobald der Termin steht" : !freigegeben ? "geht nach dem Freigeben" : !termin ? "geht, sobald der Termin steht" : null;

  // Was bestellt sein muss: die Pflicht-Teile und jedes Teil, für das es eine Bestellung gibt. Offen ist ein
  // Teil ohne Bestellung oder mit einer, die noch nicht erledigt ist.
  const teile = new Set<BestellTeil>([...PFLICHT_TEILE, ...f.bestellungen.map((b) => b.teil)]);
  const pflichtOhneZeile = PFLICHT_TEILE.some((t) => !f.bestellungen.some((b) => b.teil === t));
  const nochOffen = BESTELL_TEILE.filter(
    (t) => teile.has(t) && (!f.bestellungen.some((b) => b.teil === t) || f.bestellungen.some((b) => b.teil === t && b.erledigtAm === null)),
  );
  const bestellt = ausZeilen(
    f.bestellungen,
    pflichtOhneZeile,
    "bestellt",
    { erledigt: "bestellt", offen: `noch offen: ${nochOffen.map((t) => BESTELL_TEIL_NAMEN[t]).join(", ")}` },
    vorbedingung,
  );

  const dachZeilen = f.einsaetze.filter((e) => e.art === "dach");
  const bodenZeilen = f.einsaetze.filter((e) => e.art === "boden");
  const dachpartie = ausZeilen(dachZeilen, false, "dachpartie", { erledigt: "beauftragt", offen: "noch keine Dachpartie beauftragt" }, vorbedingung);
  const bodenpartie = ausZeilen(bodenZeilen, false, "bodenpartie", { erledigt: "eingeteilt", offen: "noch keine Bodenpartie eingeteilt" }, vorbedingung);

  // Die K2-Liste geht mit der Unterkonstruktion an den Lieferanten: Kommt danach eine andere (S7), bleibt
  // „bestellt“ erledigt, mit Hinweis – wie bei der Dachpartie.
  const unterkonstruktion = f.bestellungen.filter((b) => b.teil === "unterkonstruktion");
  if (istErledigt(bestellt.stand) && spaeter(f.k2ExcelGeaendertAm, zuletztErledigt(unterkonstruktion))) {
    bestellt.hinweis = "K2-Liste nach dem Bestellen geändert – erneut senden?";
  }
  // Was nach dem Beauftragen von draußen kam (Befund 9): erledigt bleibt erledigt, mit Hinweis.
  if (istErledigt(dachpartie.stand) && spaeter(neuer(f.letzterNachtragAm, f.k2GeaendertAm), zuletztErledigt(dachZeilen))) {
    dachpartie.hinweis = "Nach dem Beauftragen kam etwas dazu – Unterlage erneut senden?";
  }
  if (istErledigt(bodenpartie.stand) && spaeter(f.letzterNachtragAm, zuletztErledigt(bodenZeilen))) {
    bodenpartie.hinweis = "Nach dem Einteilen kam ein Nachtrag – Unterlage erneut drucken?";
  }

  const davorErledigt = [bestellt, dachpartie, bodenpartie].every((s) => istErledigt(s.stand));
  const montiert: Teilstand =
    f.montiertAm !== null ? erledigt("montiert") : davorErledigt ? offen("nach der Montage „montiert am“ eintragen") : gehtNochNicht("geht nach Bestellung, Dachpartie und Bodenpartie");

  const staende: Record<KettenSchritt, Teilstand> = {
    besichtigung,
    termin: terminSchritt,
    dachplanung,
    freigeben,
    bestellt,
    dachpartie,
    bodenpartie,
    montiert,
  };
  const fertig = f.montiertAm !== null;
  const schritte = KETTEN_SCHRITTE.map((schritt): SchrittInDerKette => {
    const s = staende[schritt];
    // Montiert: alles erledigt – was nie abgehakt wurde, sagt warum; Hinweise braucht es nicht mehr.
    if (fertig) return { schritt, name: KETTEN_SCHRITT_NAMEN[schritt], ...(istErledigt(s.stand) ? { ...s, hinweis: null } : erledigt("die Anlage ist montiert")) };
    return { schritt, name: KETTEN_SCHRITT_NAMEN[schritt], ...s };
  });
  return { schritte, jetzt: schritte.filter((s) => s.stand === "offen" || s.stand === "wieder-offen").map((s) => s.schritt), fertig };
}

/** Das Jüngere von zwei Zeitpunkten – null: keiner. */
function neuer(a: string | null, b: string | null): string | null {
  return spaeter(a, b) ? a : b;
}

/** Ein Schritt der Kette. */
export function schrittIn(kette: Kette, schritt: KettenSchritt): SchrittInDerKette {
  // Die Kette hat immer alle acht (ketteRechnen).
  return kette.schritte.find((s) => s.schritt === schritt)!;
}

// --- Die Kiste -----------------------------------------------------------------------------------

/** Durch diese Schritte „wandert“ die Anlage – je mit ihrer Kiste (daten/kisten.json); Termin und Dachplanung sind Aufgaben. */
const WEG: readonly (readonly [KettenSchritt, string])[] = [
  ["besichtigung", "doku"],
  ["freigeben", "doku"],
  ["bestellt", "bestellung"],
  ["dachpartie", "montage"],
  ["bodenpartie", "montage"],
  ["montiert", "montage"],
];

/** Nach der Montage – die nächste Station (noch ein Platzhalter, Grundsatz 8). */
export const KISTE_NACH_DER_MONTAGE = "inbetriebnahme";

/**
 * Die Kiste der Anlage aus ihrer Kette: die des ersten Schritts auf dem Weg,
 * der noch nicht erledigt ist; montiert: die Inbetriebnahme. Der Server setzt
 * damit `anlage.kiste` (server/src/crm/kette.ts) – nie von Hand; Migration
 * 0010 rechnet dasselbe in SQL (der Test vergleicht beide).
 */
export function kisteAusKette(kette: Kette): string {
  if (kette.fertig) return KISTE_NACH_DER_MONTAGE;
  for (const [schritt, kiste] of WEG) if (!istErledigt(schrittIn(kette, schritt).stand)) return kiste;
  return KISTE_NACH_DER_MONTAGE;
}

// --- „Zu tun“ ------------------------------------------------------------------------------------

/**
 * Die Schritte, die das Büro in „Zu tun“ als Knopf bekommt – in dieser
 * Reihenfolge: „Freigeben“ zuerst wie bisher (PLAN.md, E3c, „Woran wir es
 * erkennen“), dann der Termin, dann Bestellung und Partien.
 */
export const ZU_TUN_SCHRITTE = ["freigeben", "termin", "bestellt", "dachpartie", "bodenpartie"] as const satisfies readonly KettenSchritt[];
export type ZuTunSchritt = (typeof ZU_TUN_SCHRITTE)[number];

/**
 * Was das Büro an dieser Anlage jetzt tun kann – die Knöpfe in „Zu tun“:
 * Freigeben, Bestellen, Dachpartie, Bodenpartie, wenn sie offen oder wieder
 * offen sind, und erledigte mit Hinweis („Unterlage erneut senden?“). Der
 * Termin nur, solange er etwas aufhält (eine der drei nach dem Freigeben ist
 * noch nicht erledigt) – sonst stünde eine Anlage, die vor dem Update ohne
 * System bestellt und eingeteilt wurde, für immer in „Zu tun“. Die
 * Dachplanung ist kein Knopf: Sie kommt meist von draußen und hält nichts auf.
 */
export function zuTunSchritte(kette: Kette): ZuTunSchritt[] {
  if (kette.fertig) return [];
  const nachDemFreigeben = (["bestellt", "dachpartie", "bodenpartie"] as const).some((s) => !istErledigt(schrittIn(kette, s).stand));
  return ZU_TUN_SCHRITTE.filter((schritt) => {
    const s = schrittIn(kette, schritt);
    if (schritt === "termin") return nachDemFreigeben && kette.jetzt.includes("termin");
    return kette.jetzt.includes(schritt) || s.hinweis !== null;
  });
}

/**
 * Gehört die Anlage auf „Zu tun“? Wenn es dort einen Knopf gibt – oder das
 * Büro etwas ansehen soll, das nach der Freigabe kam („bitte ansehen“, wie
 * bisher). Nur Anlagen mit gesendeter Besichtigung – was draußen noch
 * aufgenommen wird, gehört dem Verkauf.
 */
export function aufZuTun(kette: Kette, bitteAnsehen: boolean): boolean {
  if (kette.fertig || !istErledigt(schrittIn(kette, "besichtigung").stand)) return false;
  return bitteAnsehen || zuTunSchritte(kette).length > 0;
}

/** „Alles bis zur Montage schon erledigt“ geht, wenn freigegeben ist und von Bestellt, Dachpartie, Bodenpartie noch etwas nicht erledigt ist. */
export function schonErledigtMoeglich(kette: Kette): boolean {
  if (kette.fertig || !istErledigt(schrittIn(kette, "freigeben").stand)) return false;
  return (["bestellt", "dachpartie", "bodenpartie"] as const).some((s) => !istErledigt(schrittIn(kette, s).stand));
}
